import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Calendar, FileText, ChevronRight, Sparkles, Trash2, AlertTriangle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiDel } from '../lib/api';
import { toast } from 'sonner';

export default function ProjectPlanningList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(false);
  const [pickPid, setPickPid] = useState('');
  const [flowType, setFlowType] = useState('auto');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try { setPlans(await apiGet('/project-planning/plans')); } catch {}
      try {
        // Only show projects that are actively running. Hide Completed / Archived /
        // Cancelled / On-Hold from the picker so planners aren't building schedules
        // for finished sites by mistake.
        const all = await apiGet('/projects');
        const INACTIVE = new Set(['completed', 'archived', 'cancelled', 'closed', 'on hold', 'on-hold']);
        const active = (all || []).filter(p => !INACTIVE.has((p.status || 'Active').toLowerCase()));
        setProjects(active);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const createPlan = async () => {
    if (!pickPid) { toast.error('Pick a project'); return; }
    try {
      const body = { project_id: pickPid };
      if (flowType !== 'auto') body.flow_type = flowType;
      const r = await apiPost('/project-planning/plans', body);
      toast.success('Plan ready · upload docs to begin');
      setPicker(false); setPickPid(''); setFlowType('auto');
      navigate(`/app/project-planning/${r.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create plan');
    }
  };

  const deletePlan = async (planId) => {
    setDeleting(true);
    try {
      await apiDel(`/project-planning/plans/${planId}`);
      setPlans(ps => ps.filter(p => p.id !== planId));
      toast.success('Plan deleted');
      setConfirmDeleteId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete plan');
    } finally {
      setDeleting(false);
    }
  };

  const phaseBadge = (p) => {
    const colors = {
      documents: 'bg-slate-100 text-slate-700',
      wbs: 'bg-sky-100 text-sky-700',
      boq_draft: 'bg-violet-100 text-violet-700',
      block_schedule: 'bg-indigo-100 text-indigo-700',
      micro_schedule: 'bg-amber-100 text-amber-700',
      material_sheet: 'bg-teal-100 text-teal-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return colors[p] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="project-planning-list">
      <PageHeader
        overline="AI-driven · Documents → WBS → Block → Micro → Material Sheet"
        title="Project Planning"
        subtitle="Generate a complete project plan from your tender documents. Approve through Planner → PM → MD."
        actions={
          <button data-testid="pp-new-plan-btn" onClick={() => setPicker(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658]">
            <Plus size={15} /> New Plan
          </button>
        }
      />

      {loading ? (
        <div className="text-center py-16 text-slate-400 text-sm">Loading…</div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-sm">
          <div className="text-xs uppercase tracking-widest font-display text-slate-400 mb-2">No plans yet</div>
          <button onClick={() => setPicker(true)} className="text-[#3b4872] text-sm font-display font-semibold flex items-center gap-1 mx-auto">
            <Sparkles size={13} /> Create your first AI plan
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="pp-plan-list">
          {plans.map(p => (
            <div key={p.id} onClick={() => navigate(`/app/project-planning/${p.id}`)} data-testid={`pp-plan-${p.id}`}
              className="bg-white border border-slate-200 hover:border-[#3b4872]/50 rounded-sm p-4 cursor-pointer flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="kpi-overline text-[#3b4872]">{p.project_code}</div>
                <div className="font-display font-bold text-base truncate">{p.project_name || p.title}</div>
                <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                  <span><FileText size={11} className="inline" /> {p.doc_count || 0} docs</span>
                  <span>· {p.block_count || 0} blocks</span>
                  <span>· {p.micro_count || 0} micro-tasks</span>
                  {(p.machine_version || 1) >= 2
                    ? <span className="text-[#3b4872] font-semibold">· New flow (v2)</span>
                    : <span className="text-amber-600 font-semibold">· Legacy flow (v1)</span>}
                </div>
              </div>
              <span className={`px-2.5 py-1 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest ${phaseBadge(p.phase)}`}>{p.phase?.replace('_', ' ')}</span>
              <button
                data-testid={`pp-delete-plan-${p.id}`}
                onClick={e => { e.stopPropagation(); setConfirmDeleteId(p.id); }}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-sm transition-colors"
                title="Delete this plan"
              >
                <Trash2 size={15} />
              </button>
              <ChevronRight size={16} className="text-slate-400" />
            </div>
          ))}
        </div>
      )}

      {picker && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-lg p-5 space-y-4">
            <h3 className="font-display font-bold text-lg">Pick an active project</h3>
            <p className="text-[11px] text-slate-500 -mt-2">Only currently-active projects are shown. Completed, archived, on-hold and cancelled projects are hidden.</p>
            {projects.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900" data-testid="pp-no-active-projects">
                No active projects found. <a href="/app/projects" className="font-display font-bold underline">Create or activate a project</a> first, then come back here.
              </div>
            ) : (
              <select value={pickPid} onChange={e => setPickPid(e.target.value)} data-testid="pp-project-select"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="">— Select project ({projects.length} active) —</option>
                {projects.map(p => <option key={p.id} value={p.id}>{`${p.code} · ${p.name}`}</option>)}
              </select>
            )}
            <div>
              <label className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Planning sequence</label>
              <select value={flowType} onChange={e => setFlowType(e.target.value)} data-testid="pp-flow-select"
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm bg-white">
                <option value="auto">Auto — from project contract type</option>
                <option value="item_rate">Item Rate flow · BOQ → WBS → Block → Micro</option>
                <option value="design_build">Design & Build flow · WBS → BOQ → Block → Micro</option>
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Item Rate: client BOQ drives the WBS. Design & Build / Lumpsum / EPC: we author the WBS first, then derive the BOQ.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setPicker(false)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm">Cancel</button>
              <button onClick={createPlan} disabled={projects.length === 0} data-testid="pp-create-confirm" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50 disabled:cursor-not-allowed">Create Plan</button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-bold text-base">Delete this plan?</h3>
                <p className="text-xs text-slate-500 mt-0.5">This will permanently remove the plan, WBS, material sheet, and all related data. This cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDeleteId(null)} disabled={deleting}
                className="px-4 py-2 text-sm border border-slate-300 rounded-sm disabled:opacity-50">
                Cancel
              </button>
              <button onClick={() => deletePlan(confirmDeleteId)} disabled={deleting}
                data-testid="pp-confirm-delete-btn"
                className="px-4 py-2 text-sm bg-rose-600 text-white rounded-sm font-display font-semibold hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2">
                {deleting ? 'Deleting…' : 'Yes, delete plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
